import MapmakerConfig from "@mapmaker/config";
import {
  BoundingBox,
  OpeningImageInput,
  OpeningImageTransform,
} from "@mapmaker/core";
import { btoa } from "isomorphic-base64";
import { DetailedHTMLProps, HTMLAttributes, ImgHTMLAttributes } from "react";

export function pow2ceil(v: number): number {
  if (v <= 0) {
    return 0;
  } else if (v <= 1) {
    return 1;
  } else if (v <= 2) {
    return 2;
  }
  var p = 2;
  while ((v >>= 1)) {
    p <<= 1;
    if (v === 2) {
      break;
    }
  }
  return p * 2;
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

type GetResizedImageUrlOptions = {
  minEdgeSize: number;
  maxEdgeSize: number;
};

const GetResizedImageUrlOptionDefaults: GetResizedImageUrlOptions = {
  minEdgeSize: 64,
  maxEdgeSize: 4096,
};

const MIN_JPEG_OUTPUT_AREA = 4_000_000;

// Edits info: https://sharp.pixelplumbing.com/api-resize
// Filter info: https://docs.aws.amazon.com/solutions/latest/serverless-image-handler/appendix-d.html
export function getResizedImageProps(
  key: string,
  desiredWidth: number,
  desiredHeight: number,
  options: Partial<GetResizedImageUrlOptions> = {}
): Pick<
  DetailedHTMLProps<ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>,
  "src" | "srcSet"
> {
  return {
    src: getResizedImageUrl(key, desiredWidth, desiredHeight, options),
    srcSet: getResizedImageSrcSet(key, desiredWidth, desiredHeight, options),
  };
}

// Edits info: https://sharp.pixelplumbing.com/api-resize
// Filter info: https://docs.aws.amazon.com/solutions/latest/serverless-image-handler/appendix-d.html
export function getResizedImageUrl(
  key: string,
  desiredWidth: number,
  desiredHeight: number,
  options: Partial<GetResizedImageUrlOptions> = {}
): string {
  const { minEdgeSize, maxEdgeSize } = {
    ...GetResizedImageUrlOptionDefaults,
    ...options,
  };
  const edgeSize = clamp(
    pow2ceil(Math.max(desiredWidth, desiredHeight)),
    minEdgeSize, // Min edge size
    maxEdgeSize // Max edge size
  );

  // Large PNG responses can exceed Lambda's synchronous response limit after base64 encoding. Force
  // image requests at 4 MP or larger to JPEG so the image handler can return them successfully.
  const returnJpeg = edgeSize * edgeSize >= MIN_JPEG_OUTPUT_AREA;
  const jpegQuality = edgeSize > 3000 ? 75 : 90;

  const imageRequest = JSON.stringify({
    bucket: MapmakerConfig.imageServerBucket,
    key,
    ...(returnJpeg ? { outputFormat: "jpeg" } : {}),
    edits: {
      resize: {
        width: edgeSize,
        height: edgeSize,
        fit: "inside",
      },
      ...(returnJpeg ? { jpeg: { quality: jpegQuality } } : {}),
    },
  });

  return `${MapmakerConfig.userContentImageServerUrl}/${btoa(imageRequest)}`;
}

/**
 * Returns a srcset string at 1x and 2x the size. Specify the desired size at 1x.
 */
export function getResizedImageSrcSet(
  key: string,
  desiredWidth: number,
  desiredHeight: number,
  options: Partial<GetResizedImageUrlOptions> = {}
): string {
  const at1x = getResizedImageUrl(key, desiredWidth, desiredHeight, options);
  const at2x = getResizedImageUrl(
    key,
    desiredWidth * 2,
    desiredHeight * 2,
    options
  );
  return [`${at1x} 1x`, `${at2x} 2x`].join(",");
}

/**
 * Returns a URL to the raw image instead of a resized one.
 */
export function getRawImageUrl(key: string): string {
  return `https://${MapmakerConfig.imageServerBucket}.s3.us-east-1.amazonaws.com/${key}`;
}

/*
 * Determines if one opening image is completely contained by another. Useful for determining if an
 * opening image is completely occluded by another.
 */
export function openingImageContains(
  container: OpeningImageTransform,
  contained: OpeningImageTransform
): boolean {
  const bPoints = getCornerPoints(contained);
  return bPoints.every((point) => rectContainsPoint(container, point));
}

/**
 * Returns only the images which can contribute pixels inside an opening. Images are drawn in array
 * order, so once a foreground image covers the opening's entire bounding box, none of the images
 * behind it need to be rendered or loaded.
 *
 * Using the complete bounding box is deliberately conservative: every point in the opening is
 * inside it, regardless of how irregular the opening path is.
 */
export function getOpeningImagesToRender(
  images: OpeningImageInput[],
  openingBounds: BoundingBox
): OpeningImageInput[] {
  for (let imageDepth = images.length - 1; imageDepth >= 0; imageDepth--) {
    if (
      openingImageContains(images[imageDepth], {
        ...openingBounds,
        rotation: 0,
      })
    ) {
      return images.slice(imageDepth);
    }
  }

  return images;
}

// Get the corner points adjusted for rotation
function getCornerPoints(rect: OpeningImageTransform) {
  const { x, y, width, height, rotation } = rect;
  const angle = (rotation * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const topLeft = {
    x: x + cos * -halfWidth - sin * -halfHeight + halfWidth,
    y: y + sin * -halfWidth + cos * -halfHeight + halfHeight,
  };
  const topRight = {
    x: x + cos * halfWidth - sin * -halfHeight + halfWidth,
    y: y + sin * halfWidth + cos * -halfHeight + halfHeight,
  };
  const bottomLeft = {
    x: x + cos * -halfWidth - sin * halfHeight + halfWidth,
    y: y + sin * -halfWidth + cos * halfHeight + halfHeight,
  };
  const bottomRight = {
    x: x + cos * halfWidth - sin * halfHeight + halfWidth,
    y: y + sin * halfWidth + cos * halfHeight + halfHeight,
  };
  return [topLeft, topRight, bottomLeft, bottomRight];
}

// determine if a rectangle contains a point, including the rotation of the rectangle.
function rectContainsPoint(
  rect: OpeningImageTransform,
  point: { x: number; y: number }
): boolean {
  const center = {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
  const angle = (-rect.rotation * Math.PI) / 180;
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const localX = dx * Math.cos(angle) - dy * Math.sin(angle);
  const localY = dx * Math.sin(angle) + dy * Math.cos(angle);

  return (
    localX >= -rect.width / 2 &&
    localX <= rect.width / 2 &&
    localY >= -rect.height / 2 &&
    localY <= rect.height / 2
  );
}
